import { useState } from 'react';
import { MenuProps, Flex } from 'antd';
import { MoreOutlined } from '@ant-design/icons';

import { MDropdown } from '@Components/Base';
import useUserPermissions from '@Hooks/useUserPermissions';
import { MongoAbility } from '@casl/ability';
import { ReactComponent as ViewIcon } from '@Icons/additional/eye.svg';
import { ReactComponent as ImportIcon } from '@Icons/additional/import.svg';
import { ReactComponent as ExportIcon } from '@Icons/additional/export.svg';
import { ReactComponent as DeleteIcon } from '@Icons/additional/trash.svg';
import RequiredNotification from '@Components/RequiredNotification/RequiredNotification';

interface TableManageRequestsActionsProps {
  status: string;
  requestNo: number;
  onView: () => void;
  onImport: () => void;
  onExport: () => void;
  onDelete: () => void;
}

const items = ({
  status,
  permissions,
  onView,
  onImport,
  onExport,
  onDelete,
}: Omit<TableManageRequestsActionsProps, 'requestNo'> & {
  permissions: MongoAbility;
}): MenuProps['items'] => {
  const statusLC = status.toLowerCase();

  const common = {
    style: {
      padding: 0,
    },
  };

  const viewItem = permissions.can('Get', 'PurchaseRequest')
    ? {
        ...common,
        label: (
          <Flex className="trojan-action-item" onClick={onView}>
            <ViewIcon /> <span>View</span>
          </Flex>
        ),
        key: '0',
      }
    : null;
  const importItem = permissions.can('Import', 'PurchaseRequest')
    ? {
        ...common,
        label: (
          <Flex className="trojan-action-item" onClick={onImport}>
            <ImportIcon /> <span>Import PR</span>
          </Flex>
        ),
        key: '1',
      }
    : null;
  const exportItem = permissions.can('Export', 'PurchaseRequest')
    ? {
        ...common,
        label: (
          <Flex className="trojan-action-item" onClick={onExport}>
            <ExportIcon /> <span>Export Excel</span>
          </Flex>
        ),
        key: '2',
      }
    : null;
  const deleteItem =
    permissions.can('Delete', 'PurchaseRequest') &&
    statusLC === 'awaiting tender creation'
      ? {
          ...common,
          label: (
            <Flex className="trojan-action-item" onClick={onDelete}>
              <DeleteIcon />
              <span>Delete</span>
            </Flex>
          ),
          key: '3',
        }
      : null;

  const output: MenuProps['items'] = [
    viewItem,
    importItem,
    exportItem,
    deleteItem,
  ];
  return output;
};

const TableManageRequestsActions: React.FC<TableManageRequestsActionsProps> = ({
  status,
  requestNo,
  onView,
  onImport,
  onExport,
  onDelete,
}) => {
  const permissions = useUserPermissions();
  const [showDeleteNotification, setShowDeleteNotification] = useState(false);

  const openDeleteNotification = (): void => setShowDeleteNotification(true);
  const closeDeleteNotification = (): void => setShowDeleteNotification(false);

  const performDelete = (): void => {
    closeDeleteNotification();
    onDelete();
  };

  return (
    <div>
      <Flex justify="center">
        <MDropdown
          menu={{
            items: items({
              status,
              onView,
              onImport,
              onExport,
              onDelete: openDeleteNotification,
              permissions,
            }),
          }}
          trigger={['click']}
        >
          <MoreOutlined />
        </MDropdown>
      </Flex>
      <RequiredNotification
        title="Delete Request"
        message={`Are you sure you want to delete request no.${requestNo}?`}
        isModalOpen={showDeleteNotification}
        textSuccessBtn="Yes, Delete"
        textCancelBtn="No, Thanks"
        handleCancel={closeDeleteNotification}
        handleSuccess={performDelete}
        isDeleteNotification
      />
    </div>
  );
};

export default TableManageRequestsActions;
